import "./tableView.scss";

import { List } from "immutable";
import { debounce, defer, isEqual, isNil, uniq } from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import { findDOMNode } from "react-dom";
import ImmutablePropTypes from "react-immutable-proptypes";
import { PopoverBody } from "reactstrap";

import FocusedPopover from "../../components/popover/FocusedPopover";
import KeyConstants from "../../constants/keyConstants";
import outlineColumnIds from "../../constants/outlineColumnIds";
import PubSubTopics from "../../constants/pubSubTopics";
import tableRowTypes from "../../constants/tableRowTypes";
import timeoutRates from "../../constants/timeoutRates";
import dialogs from "../../dialogs";
import { createFromJS, immutableRecords, PropTypeShapes } from "../../types";
import { TableCellSelectionRecord } from "../../types/immutableRecords/display/tableView/TableCellSelectionRecord";
import { isPastingItems } from "../../utils/copyPaste";
import { getModifierKeysForEvent, hasMultipleModifiers } from "../../utils/getModifierKeys";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import isEditable from "../../utils/isEditable";
import { updateSelectedRowsArray } from "../../utils/selection";
import uniqueId from "../../utils/uniqueId";
import CheckboxListView from "../checkboxList/CheckboxListView";
import TableCanvasView from "./TableCanvasView";
import utils from "./utils";

const SelectAction = {
  ADD_TO_SELECTION: "ADD_TO_SELECTION",
  REPLACE_LAST_SELECTION: "REPLACE_LAST_SELECTION",
  REPLACE_SELECTION: "REPLACE_SELECTION"
};

const cellRangeForCells = (cell1, cell2) => {
  const top = Math.min(cell1.row, cell2.row);
  const bottom = Math.max(cell1.row, cell2.row);
  const left = Math.min(cell1.column, cell2.column);
  const right = Math.max(cell1.column, cell2.column);
  const topLeft = immutableRecords.TableCellPositionRecord({
    row: top,
    column: left
  });
  const bottomRight = immutableRecords.TableCellPositionRecord({
    row: bottom,
    column: right
  });
  return [topLeft, bottomRight];
};

class TableView extends PureComponent {
  constructor(props, context) {
    super(props, context);

    // first selected row index
    const rowIndex = this.props.data.rows.findIndex(r => this.props.selectedRowIds.contains(r.id));

    // Default cell
    const focusedCell = immutableRecords.TableCellPositionRecord({
      row: rowIndex > 0 ? rowIndex : 0,
      column: 0
    });
    const selectedGroup = immutableRecords.TableCellGroupRecord({
      topLeft: immutableRecords.TableCellPositionRecord(focusedCell.toObject()),
      bottomRight: immutableRecords.TableCellPositionRecord(focusedCell.toObject())
    });
    const cellSelection = TableCellSelectionRecord({ groups: List([selectedGroup]) });

    this.state = {
      popoverOpen: false,
      cellSelection,
      focusedCell,
      tableHasFocus: false,
      cellPopoverOpen: false,
      scrollLeft: 0,
      scrollTop: 0,
      dropRow: null,
      dropAction: null,
      dimensions: {
        width: -1,
        height: -1
      }
    };

    this._cellFocusCallbacks = {};

    this._newRowComing = false;
    this._pendingScrollToCellData = null;
    this._pendingTableHasFocus = false;

    // Indicate adding item by Tab.
    // To avoid next adjust cell selection in componentWillReceiveProps
    // See https://aeontimeline.atlassian.net/browse/ATR-1116?focusedCommentId=11862
    // To avoid focusing the new item when adding by typing in empty row
    this._tabToAddNewItem = false;
    // Indicate adding item by Enter.
    // To avoid focusing the new item when adding by typing in empty row
    this._enterToAddNewItem = false;

    this._nextState = this.state;

    this._tableId = "x" + uniqueId();

    this._selectOriginCell = null;
    this._selectDestinationCell = null;

    // Used for updating the cell selections
    this._pastedData = false;

    this.setAndTransferState = this.setAndTransferState.bind(this);

    this.debouncedTransferState = debounce(this.setAndTransferState, timeoutRates.TABLE_FOCUS_CHANGE_TIME);

    this.registerCellFocusCallback = this.registerCellFocusCallback.bind(this);
    this.unregisterCellFocusCallback = this.unregisterCellFocusCallback.bind(this);
    this.signalCellToAcquireFocus = this.signalCellToAcquireFocus.bind(this);
    this.onReceivedFocus = this.onReceivedFocus.bind(this);

    this.onTableClick = this.onTableClick.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.moveFocus = this.moveFocus.bind(this);
    this.onLiveRowDrag = this.onLiveRowDrag.bind(this);
    this.onDragEnter = this.onDragEnter.bind(this);
    this.onDragLeave = this.onDragLeave.bind(this);
    this.onRowDrop = this.onRowDrop.bind(this);
    this.onRowGutterClick = this.onRowGutterClick.bind(this);
    this.onCellClick = this.onCellClick.bind(this);
    this.onCellDoubleClick = this.onCellDoubleClick.bind(this);
    this.onCellPopoverToggle = this.onCellPopoverToggle.bind(this);
    this.onCellCut = this.onCellCut.bind(this);
    this.onCellCopy = this.onCellCopy.bind(this);
    this.onCellPaste = this.onCellPaste.bind(this);
    this.onCellDelete = this.onCellDelete.bind(this);
    this.toggleColumnPopover = this.toggleColumnPopover.bind(this);
    this.selectCellWithId = this.selectCellWithId.bind(this);
    this.getCellOptions = this.getCellOptions.bind(this);

    this.requestViewBoundsForZoom = this.requestViewBoundsForZoom.bind(this);

    this.onValueChanged = this.onValueChanged.bind(this);
    this.onMultiValueChanged = this.onMultiValueChanged.bind(this);

    this.getCellId = this.getCellId.bind(this);

    this.tableHasFocus = this.tableHasFocus.bind(this);
    this.onCutMessageReceived = this.onCutMessageReceived.bind(this);
    this.onCopyMessageReceived = this.onCopyMessageReceived.bind(this);
    this.onPasteMessageReceived = this.onPasteMessageReceived.bind(this);

    this.onPaste = this.onPaste.bind(this);

    this.selectRow = this.selectRow.bind(this);
    this.debouncedSelectRow = debounce((rowId, modifiers, selectFromKeyPress) => {
      this.selectRow(rowId, modifiers, selectFromKeyPress);
    }, timeoutRates.SELECT_ROW_BY_KEY_PRESS_THROTTLE);
  }

  componentDidMount() {
    this._node = findDOMNode(this);
    this._pubSubCutToken = PubSub.subscribe(PubSubTopics.cut, this.onCutMessageReceived);
    this._pubSubCopyToken = PubSub.subscribe(PubSubTopics.copy, this.onCopyMessageReceived);
    this._pubSubPasteToken = PubSub.subscribe(PubSubTopics.paste, this.onPasteMessageReceived);
    this.storeLastStateAndProps();
    this._deferredAcquireFocusTimer = defer(this.signalCellToAcquireFocus);
  }

  componentWillReceiveProps(nextProps) {
    // debugUtils.logPropChanges("TableView", this.props, nextProps, true);
    if (this._pendingScrollToCellData) {
      if (this._pendingScrollToCellData.row === null) {
        const oldRows = this.props.data.rows.map(r => r.id).toSet();
        const newRows = nextProps.data.rows.map(r => r.id).toSet();
        const addedRows = newRows.subtract(oldRows).toArray();
        if (addedRows.length > 0) {
          this._pendingScrollToCellData.row = addedRows[0];
        }
      }
      this.selectCellWithId(nextProps.data, this._pendingScrollToCellData.row, this._pendingScrollToCellData.column);
    }

    if (this._pastedData && !nextProps.data.rows.equals(this.props.data.rows)) {
      const pastedData = this._pastedData;
      this._pastedData = null;
      const groups = pastedData.map(pd => {
        const rowIndex = nextProps.data.rows.findIndex(r => r.id === pd.rowId);
        const colIndex = nextProps.data.columns.findIndex(c => c.id === pd.columnId);
        return createFromJS.TableCellGroupRecord({
          topLeft: { row: rowIndex, column: colIndex },
          bottomRight: { row: rowIndex, column: colIndex }
        });
      });
      const newSelection = utils.mergeSelectionIfRect(TableCellSelectionRecord({ groups: groups }));
      this.setAndTransferState({ cellSelection: newSelection });
    } else if (this.props.data.rows !== nextProps.data.rows || this.props.data.columns !== nextProps.data.columns) {
      if (
        !this._tabToAddNewItem &&
        !this._enterToAddNewItem &&
        this.props.data.rows.size < nextProps.data.rows.size &&
        !this.props.selectedRowIds.equals(nextProps.selectedRowIds)
      ) {
        // New items are coming from Sidebar, menu, shortcuts
        // should select the new selected item label
        // See ATR-1167
        // See ATR-1763
        this.selectNewAndSelectedItem(nextProps.selectedRowIds, nextProps.data);
      } else {
        // Technically, we only need to run this if rows or columns change order or reduce in length
        // But we will run it permanently for now, as it has a check to see if it ends up at the same
        // result
        // Note: the above if case is a special one
        // if that one is not matching, we always fall into this adjustment
        this.adjustSelectionAndFocusOnPropsChange(
          this._nextState.cellSelection,
          this._nextState.focusedCell,
          this.props.data,
          nextProps.data
        );
      }
    }
  }

  componentDidUpdate() {
    // Handle any necessary focus changes
    // Table View has focus, but has gone from active to not active view:
    //   - We should give up focus
    if (this.state.tableHasFocus && this._lastProps.isActiveView && !this.props.isActiveView) {
      document.activeElement.blur();
    } else {
      let shouldFocus = false;
      // Table View does not have focus, but has just become active:
      //   - Set focus to the table view cell
      if (!this.state.tableHasFocus && !this._lastProps.isActiveView && this.props.isActiveView) {
        shouldFocus = true;
      }
      // Table View focused cell has changed:
      //   - Set focus to the table view cell
      // Note: if focused cell is changing, we assume we must want focus in it
      if (
        this.state.tableHasFocus &&
        !utils.areCellsSame(this.state.focusedCell, this._lastState.focusedCell, this.props.data, this._lastProps.data)
      ) {
        shouldFocus = true;
      }
      // Table View is active, a cell popover has just closed, and document.activeElement is body
      //   - Set focus to the table view cell

      if (
        this.props.isActiveView &&
        !this.state.cellPopoverOpen &&
        this._lastState.cellPopoverOpen &&
        document.activeElement === document.body
      ) {
        shouldFocus = true;
      }
      // Is active view, table has no focus, new items coming, and selected changed
      // should focus to the table view cell
      // See ATR-1167
      if (
        this.props.isActiveView &&
        !this.state.tableHasFocus &&
        this._lastProps.data.rows.size < this.props.data.rows.size &&
        !this._lastProps.selectedRowIds.equals(this.props.selectedRowIds)
      ) {
        shouldFocus = true;
      }

      if (shouldFocus) {
        this._deferredAcquireFocusTimer = defer(this.signalCellToAcquireFocus);
      }
    }

    // If we didn't have focus last time and now we do, grab focus
    if (!this._lastState.tableHasFocus && this.state.tableHasFocus) {
      this._deferredReceiveFocusTimer = defer(this.onReceivedFocus);
    }

    // Store the values used to make this assessment, so we have access to them next time
    this.storeLastStateAndProps();
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubCutToken);
    PubSub.unsubscribe(this._pubSubCopyToken);
    PubSub.unsubscribe(this._pubSubPasteToken);
    this.debouncedTransferState.cancel();
    this.debouncedSelectRow.cancel();
    if (this._deferredAcquireFocusTimer) {
      window.clearTimeout(this._deferredAcquireFocusTimer);
    }
    if (this._deferredReceiveFocusTimer) {
      window.clearTimeout(this._deferredReceiveFocusTimer);
    }
  }

  selectNewAndSelectedItem(rowIds, newData) {
    const rowId = rowIds.get(0);
    if (rowId) {
      const rowIndex = newData.rows.findIndex(r => r.id === rowId);
      const columnIndex = newData.columns.findIndex(c => c.id === outlineColumnIds.label);
      const cell = immutableRecords.TableCellPositionRecord({ row: rowIndex, column: columnIndex });
      const newSelection = TableCellSelectionRecord({
        groups: List([
          immutableRecords.TableCellGroupRecord({
            topLeft: cell,
            bottomRight: cell
          })
        ])
      });
      const changedState = {
        cellSelection: newSelection,
        focusedCell: cell
      };
      this.setAndTransferState(changedState);
    }
  }

  storeLastStateAndProps() {
    this._nextState = Object.assign({}, this.state);
    this._lastState = Object.assign({}, this.state);
    this._lastProps = Object.assign({}, this.props);
  }

  /**
   * This function should be used everywhere within this component, instead of setState
   * It will call setState with the provided values, but also transfer the focus value
   * to state at the same time
   * @param {object} newState
   */
  setAndTransferState(newState) {
    // Calculate what changes we will apply
    const changedState = Object.assign({}, newState, { tableHasFocus: this._pendingTableHasFocus });

    // Create a _nextState object that has what our state will become
    // Necessary because componentWillReceiveProps() does not have an updated state yet
    this._nextState = Object.assign(this._nextState, changedState);
    // And finally, signal the change of state
    this.setState(changedState);
  }

  registerCellFocusCallback(rowId, colId, callback) {
    const cellId = this.getCellId(rowId, colId);
    this._cellFocusCallbacks[cellId] = callback;
  }

  unregisterCellFocusCallback(rowId, colId, callback) {
    const cellId = this.getCellId(rowId, colId);
    if (this._cellFocusCallbacks[cellId] === callback) {
      delete this._cellFocusCallbacks[cellId];
    }
  }

  /**
   * This function adjusts any selected cells after a props change to make sure
   * we stick to the same row/column IDs rather than indexes
   * @param {object} prevProps
   * @param {object} nextProps
   */
  adjustSelectionAndFocusOnPropsChange(cellSelection, focusedCell, oldData, newData) {
    // const groups = pastedData.map(pd => {
    //   const rowIndex = nextProps.data.rows.findIndex(r => r.id === pd.rowId);
    //   const colIndex = nextProps.data.columns.findIndex(c => c.id === pd.columnId);
    //   return createFromJS.TableCellGroupRecord({
    //     topLeft: { row: rowIndex, column: colIndex },
    //     bottomRight: { row: rowIndex, column: colIndex }
    //   });
    // });
    const updatedCellForRowColumn = (r, c) => {
      // So far it is checking for the readonly.
      // In normal mode, the table view would have at least one empty row.
      if (newData.rows.size > 0) {
        // Find the selected id from the old data rows
        // except new row is coming from Tab in empty row
        // See ATR-1118 (previous solution)
        // See https://aeontimeline.atlassian.net/browse/ATR-1116?focusedCommentId=11862
        const rowId = !this._tabToAddNewItem && oldData.rows.size > r ? oldData.rows.get(r).id : newData.rows.get(r).id;
        // Same logic as processing the rows above
        const colId =
          !this._tabToAddNewItem && oldData.columns.size > c ? oldData.columns.get(c).id : newData.columns.get(c).id;
        const row = newData.rows.findIndex(r => r.id === rowId);
        const column = newData.columns.findIndex(c => c.id === colId);
        return immutableRecords.TableCellPositionRecord({ row: row < 0 ? 0 : row, column: column < 0 ? 0 : column });
      } else {
        return immutableRecords.TableCellPositionRecord({ row: 0, column: 0 });
      }
    };

    let newGroups = [];

    cellSelection.groups.forEach(g => {
      for (let r = g.topLeft.row; r <= g.bottomRight.row; r++) {
        for (let c = g.topLeft.column; c <= g.bottomRight.column; c++) {
          const cell = updatedCellForRowColumn(r, c);
          if (!isNil(cell.row) && !isNil(cell.column)) {
            newGroups.push(
              immutableRecords.TableCellGroupRecord({
                topLeft: cell,
                bottomRight: cell
              })
            );
          }
        }
      }
    });

    const newSelection = utils.mergeSelectionIfRect(TableCellSelectionRecord({ groups: List(newGroups) }));
    const newFocusedCell = focusedCell ? updatedCellForRowColumn(focusedCell.row, focusedCell.column) : focusedCell;

    this._tabToAddNewItem = false;
    this._enterToAddNewItem = false;

    const changedState = {
      cellSelection: isEqual(cellSelection.toJS(), newSelection.toJS()) ? cellSelection : newSelection,
      focusedCell: isEqual(focusedCell.toJS(), newFocusedCell.toJS()) ? focusedCell : newFocusedCell
    };
    this.setAndTransferState(changedState);
  }

  onCellPopoverToggle(isOpen) {
    this.setAndTransferState({
      cellPopoverOpen: isOpen
    });
  }

  onFocusIn(_e) {
    // Store the value locally, but don't trigger a re-render yet
    this._pendingTableHasFocus = true;
    // We will trigger a re-render after a period of time if it hasn't already been done for us
    this.debouncedTransferState();
  }

  onFocusOut(_e) {
    // Store the value locally, but don't trigger a re-render yet
    this._pendingTableHasFocus = false;
    // We will trigger a re-render after a period of time if it hasn't already been done for us
    this.debouncedTransferState();
  }

  onReceivedFocus() {
    this._deferredReceiveFocusTimer = null;
    if (this.props.onReceivedFocus) {
      this.props.onReceivedFocus();
    }
  }

  signalCellToAcquireFocus() {
    this._deferredAcquireFocusTimer = null;
    if (this.state.focusedCell) {
      // get the focused cell
      const row = this.state.focusedCell.row;
      const column = this.state.focusedCell.column;
      const propsRow = this.props.data.rows.get(row);
      const propsCol = this.props.data.columns.get(column);
      if (!propsRow || !propsCol) {
        return;
      }
      const rowId = propsRow.id;
      const colId = propsCol.id;
      const cellId = this.getCellId(rowId, colId);
      const callback = this._cellFocusCallbacks[cellId];
      if (callback) {
        callback();
      }
    }
  }

  tableHasFocus() {
    return this._node ? this._node.contains(document.activeElement) : false;
  }

  toggleColumnPopover() {
    this.setAndTransferState({
      popoverOpen: !this.state.popoverOpen
    });
  }

  onCutMessageReceived(msg, clipboardData) {
    if (this.tableHasFocus() && !isEditable(document.activeElement) && this.state.focusedCell) {
      this.onCellCut({ clipboardData });
    }
  }

  onCopyMessageReceived(msg, clipboardData) {
    if (this.tableHasFocus() && !isEditable(document.activeElement) && this.state.focusedCell) {
      this.onCellCopy({ clipboardData });
    }
  }

  onPasteMessageReceived(msg, clipboardData) {
    this.onPaste({ clipboardData });
  }

  onCellClick(row, column, modifiers, rowId) {
    const cellPosition = immutableRecords.TableCellPositionRecord({
      row,
      column
    });
    if (modifiers.SHIFT && this._selectOriginCell) {
      // If they are holding SHIFT, then we want to create a group from the last
      // click to this one.
      const cellRange = cellRangeForCells({ row, column }, this._selectOriginCell);
      // Always replace the last selection
      this.selectCellRange(...cellRange, SelectAction.REPLACE_LAST_SELECTION);
    } else {
      // If we have a non-shift click, record it so we can use it as the starting point for any subsequent
      // shift clicks
      this._selectOriginCell = cellPosition;
      // And then go on and handle non-shift clicks normally!
      if (modifiers.CONTROL || modifiers.COMMAND) {
        // If Ctrl/Cmd, then add to selection
        this.toggleSingleCell(this._selectOriginCell, SelectAction.ADD_TO_SELECTION);
      } else {
        // Otherwise, replace entire selection
        this.selectSingleCell(this._selectOriginCell, SelectAction.REPLACE_SELECTION);
      }
    }

    // Finally, record this as the current selection "destination"...
    // This is the end point for any current selection
    this._selectDestinationCell = immutableRecords.TableCellPositionRecord({
      row,
      column
    });

    this.debouncedTransferState({
      focusedCell: cellPosition
    });

    //#!if iOS !== true
    this.selectRow(rowId, modifiers);
    //#!endif
  }

  selectRow(rowId, modifiers = {}, selectFromKeyPress = false) {
    const rows = this.props.data.rows;
    let newRowSelection = [];

    //#!if iOS === false
    if (modifiers.SHIFT && this._lastClickRowId) {
      // If this is the first shift click since some other click, record the current selection
      // as the starting point for any subsequent shift clicks
      if (!this._firstShiftClickSelection) {
        this._firstShiftClickSelection = this.props.selectedRowIds;
      }
      // Select everything from last click to current position
      const a = rows.findIndex(r => r.id === this._lastClickRowId);
      const b = rows.findIndex(r => r.id === rowId);
      const low = Math.min(a, b);
      const high = Math.max(a, b);
      const newIds = rows
        .slice(low, high + 1)
        .map(r => r.id)
        .toArray();
      // Note that we can't simply add to selection, as we may want to select a lesser amount than was
      // selected with the previous shift click.
      // So instead we add this new selection to the original selection before the shift clicks began
      newRowSelection = this._firstShiftClickSelection.push(...newIds).toArray();
    } else {
      // If we have a non-shift click, record it so we can use it as the starting point for any subsequent
      // shift clicks
      this._lastClickRowId = rowId;
      // Erase the first shift click so we start again the next time we do a shift click
      this._firstShiftClickSelection = null;

      // And then go on and handle non-shift clicks normally!
      const allowMulti = selectFromKeyPress ? false : modifiers.CONTROL || modifiers.COMMAND;
      newRowSelection = updateSelectedRowsArray(allowMulti, rowId, this.props.selectedRowIds.toArray());
    }
    //#!elseif iOS === true
// newRowSelection = updateSelectedRowsArray(this.props.isEditMode, rowId, this.props.selectedRowIds.toArray());
    //#!endif

    newRowSelection = uniq(newRowSelection);

    this.props.onSelectRows(newRowSelection);
  }

  onCellDoubleClick(_row, _column, _modifiers) {}

  toggleSingleCell(cellPosition, action = SelectAction.REPLACE_SELECTION) {
    if (utils.isCellSelected(this.state.cellSelection, cellPosition)) {
      this.deselectSingleCell(cellPosition);
    } else {
      this.selectSingleCell(cellPosition, action);
    }
  }

  deselectSingleCell(cellPosition) {
    this.setAndTransferState({
      cellSelection: utils.selectionWithoutCell(this.state.cellSelection, cellPosition)
    });
  }

  selectSingleCell(cellPosition, action = SelectAction.REPLACE_SELECTION) {
    this.selectCellRange(cellPosition, cellPosition, action);
    this._selectDestinationCell = cellPosition;
    if (action === SelectAction.REPLACE_SELECTION) {
      this._selectOriginCell = cellPosition;
    }
    this.setAndTransferState({
      focusedCell: cellPosition
    });
  }

  selectCellRange(topLeft, bottomRight, action = SelectAction.REPLACE_SELECTION) {
    let cellSelection = this.state.cellSelection;
    const group = immutableRecords.TableCellGroupRecord({
      topLeft,
      bottomRight
    });
    switch (action) {
      case SelectAction.ADD_TO_SELECTION:
        cellSelection = cellSelection.update("groups", groups => groups.push(group));
        break;
      case SelectAction.REPLACE_LAST_SELECTION:
        cellSelection = cellSelection.update("groups", groups => groups.set(groups.size - 1, group));
        break;
      default:
        cellSelection = TableCellSelectionRecord({ groups: List([group]) });
        break;
    }

    cellSelection = utils.mergeSelectionIfRect(cellSelection);

    this.setAndTransferState({
      cellSelection
    });
  }

  selectCellWithId(data, rowId, colId, action = SelectAction.REPLACE_SELECTION) {
    const rowRec = data.rows.find(r => r.id === rowId);
    const colRec = data.columns.find(c => c.id === colId);

    if (!rowRec || !colRec) {
      // eslint-disable-next-line no-console
      console.error(
        `Caught error in selectCellWithId(): no cell found for rowId = ${rowId}, colId = ${colId}, printing data.toJS(), trace`
      );
      // eslint-disable-next-line no-console
      console.dir(data ? data.toJS() : data);
      // eslint-disable-next-line no-console
      console.trace();
      return;
    }

    const cell = immutableRecords.TableCellPositionRecord({
      row: rowRec.index,
      column: colRec.index
    });

    if (action === SelectAction.REPLACE_LAST_SELECTION) {
      const cellRange = cellRangeForCells(cell, this._selectOriginCell);
      this.selectCellRange(...cellRange, action);
    } else {
      this.selectSingleCell(cell, action);
    }
    this._selectDestinationCell = cell;
  }

  scrollToRow(rowId) {
    // We would have a pending scrolling action from the moveFocus
    // which is from the case of creating a new item.
    // So we use this scrollToRow call which is triggered by creating
    // item in the middleware to avoid two scrolling actions from
    // both moveFocus and the middleware
    // See https://aeontimeline.atlassian.net/browse/ATR-417
    const scrollData = this._pendingScrollToCellData
      ? Object.assign({}, this._pendingScrollToCellData)
      : {
        tableId: this._tableId,
        row: rowId,
        column: this.props.data.columns.first().id,
        animate: true
      };
    this._pendingScrollToCellData = null;
    // Do not perform the scrolling
    // if one of the selected cells are in the target row
    if (!utils.isRowPartOfSelection(this.state.cellSelection, scrollData.row)) {
      PubSub.publish(PubSubTopics.scrollToCell, scrollData);
    }
  }

  // This function is called by child TableCellView components when they receive a key
  // event that should move focus to another cell.
  moveFocus(keyEvent) {
    // Disable multiple modifier hotkeys acting on table view
    if (hasMultipleModifiers(keyEvent)) {
      return;
    }

    const data = this.props.data;

    // If the user hits TAB or ENTER, always replace the selection, starting from the focused cell
    let action = SelectAction.REPLACE_SELECTION;
    let startingCell = this.state.focusedCell ? this.state.focusedCell : { row: 0, column: 0 };

    // If they hit an arrow key and are holding a meta key, add to the current selection instead,
    // starting with the last selected cell
    if (keyEvent.key !== KeyConstants.Tab && keyEvent.key !== KeyConstants.Enter) {
      if (keyEvent.shiftKey) {
        action = SelectAction.REPLACE_LAST_SELECTION;
        startingCell = this._selectDestinationCell ? this._selectDestinationCell : startingCell;
      } else if (keyEvent.ctrlKey || keyEvent.metaKey) {
        action = SelectAction.ADD_TO_SELECTION;
        startingCell = this._selectDestinationCell ? this._selectDestinationCell : startingCell;
      }
    }

    const startRow = startingCell.row;
    const startCol = startingCell.column;

    let nextRow = startRow;
    let nextCol = startCol;

    switch (keyEvent.key) {
      case KeyConstants.Tab: {
        action = SelectAction.REPLACE_SELECTION;
        if (keyEvent.shiftKey) {
          if (startCol <= 0) {
            nextRow--;
            nextCol = this.props.data.columns.size - 1;
          } else {
            nextCol--;
          }
        } else {
          if (startCol === this.props.data.columns.size - 1) {
            nextRow++;
            nextCol = 0;
          } else {
            nextCol++;
          }
        }
        break;
      }
      case KeyConstants.Enter:
        action = SelectAction.REPLACE_SELECTION;
        keyEvent.shiftKey ? nextRow-- : nextRow++;
        break;
      case KeyConstants.Up:
        if (keyEvent.ctrlKey || keyEvent.metaKey) {
          action = SelectAction.REPLACE_SELECTION;
          nextRow = 0;
        } else {
          nextRow--;
        }
        break;
      case KeyConstants.Down:
        if (keyEvent.ctrlKey || keyEvent.metaKey) {
          action = SelectAction.REPLACE_SELECTION;
          //#!if readonly === true
          nextRow = this.props.data.rows.size - 1;
          //#!else
// nextRow = this.props.data.rows.size - 2;
          //#!endif
        } else {
          nextRow++;
        }
        break;
      case KeyConstants.Left:
        if (keyEvent.ctrlKey || keyEvent.metaKey) {
          action = SelectAction.REPLACE_SELECTION;
          nextCol = 0;
        } else {
          nextCol--;
        }
        break;
      case KeyConstants.Right:
        if (keyEvent.ctrlKey || keyEvent.metaKey) {
          action = SelectAction.REPLACE_SELECTION;
          nextCol = this.props.data.columns.size - 1;
        } else {
          nextCol++;
        }
        break;
      default:
        break;
    }
    if (nextRow < 0) {
      nextRow = 0;
    } else if (nextRow >= this.props.data.rows.size - 1) {
      nextRow = this.props.data.rows.size - 1;
    }
    if (nextCol < 0) {
      nextCol = 0;
    } else if (nextCol >= this.props.data.columns.size - 1) {
      nextCol = this.props.data.columns.size - 1;
    }

    // Now capture the ID we want to select next...
    const nextRowId = data.rows.get(nextRow).id;
    const nextColId = data.columns.get(nextCol).id;

    // Normally, we can just trigger the selected cell change right away
    // But there is a special case when we were adding a new row...
    // We actually need to wait until the next render and pick up the new rowID.
    this._pendingScrollToNextCell = null;

    if (!this._newRowComing) {
      this.selectCellWithId(this.props.data, nextRowId, nextColId, action);
      PubSub.publish(PubSubTopics.scrollToCell, {
        tableId: this._tableId,
        row: nextRowId,
        column: nextColId
      });
    } else {
      this._newRowComing = false;

      // Note: Tricky solution for the Tab to add new item
      // Normally we adjust the cell selection when rows/columns changing.
      // But the Tab to add item is an exception, it needs selection going to
      // the row of the new items.
      // Therefore set a flag to avoid the next round of adjustSelectionAndFocusOnPropsChange
      // in componentWillReceiveProps
      // See https://aeontimeline.atlassian.net/browse/ATR-1116?focusedCommentId=11862
      if (keyEvent.key === KeyConstants.Tab) {
        this._tabToAddNewItem = true;
      }
      if (keyEvent.key === KeyConstants.Enter) {
        this._enterToAddNewItem = true;
      }

      // Adjust the row according to the combination of
      // Tab, Enter and Shift
      let row = null;
      if (keyEvent.key === KeyConstants.Enter || keyEvent.key === KeyConstants.Tab) {
        if (keyEvent.shiftKey) {
          row = nextRowId === tableRowTypes.EMPTY ? null : nextRowId;
        } else {
          row = keyEvent.key === KeyConstants.Enter ? tableRowTypes.EMPTY : null;
        }
      }
      this._pendingScrollToCellData = {
        tableId: this._tableId,
        // if Shift Enter, go to the next (above) row, if it is the only row then go to the new row
        // if Enter, always go to the empty row
        // if Tab, always go to the new row
        row: row,
        column: nextColId
      };
      this.setAndTransferState({ pendingScrollToCell: true });
    }

    //#!if iOS !== true
    if (nextRowId !== tableRowTypes.EMPTY) {
      const modifierKeys = getModifierKeysForEvent(keyEvent);
      this.debouncedSelectRow(nextRowId, modifierKeys, true);
    }
    //#!endif
  }

  onTableClick(e) {
    const clickWasRow = hasAncestorOfClass(e.target, "tableRow");
    if (!clickWasRow) {
      this.props.onSelectRows([]);
    }
  }

  onRowGutterClick(rowId, modifiers) {
    // Clicking in the gutter should select rows and remove focus on cells
    // Ignore the empty row
    if (!rowId) {
      return;
    }

    const rows = this.props.data.rows;
    this.selectRow(rowId, modifiers);

    this.selectSingleCell(
      immutableRecords.TableCellPositionRecord({
        row: rows.findIndex(r => r.id === rowId),
        column: 0
      })
    );
  }

  onLiveRowDrag(itemIds, dropRow, dropAction) {
    if (!this.props.allowRowDragNesting) {
      this.props.onLiveRowDrag(itemIds, dropRow, dropAction);
    } else {
      // We just want to draw a marker line
      this.setAndTransferState({
        dropRow,
        dropAction
      });
    }
  }

  onDragEnter(itemIds) {
    this.props.onStartRowDrag(itemIds);
  }

  onDragLeave() {
    if (!this.props.allowRowDragNesting) {
      this.props.onDragLeave();
    } else {
      this.setAndTransferState({
        dropRow: null,
        dropAction: null
      });
    }
  }

  onRowDrop(itemIds, dropRow, dropAction, fromExternalSource) {
    // Send the drop info to owner
    this.props.onRowDrop(itemIds, dropRow, dropAction, fromExternalSource);
    //#!if iOS !== true
    this.props.onSelectRows(itemIds);
    //#!endif

    if (this.props.allowRowDragNesting) {
      // And if necessary, clear our drawn drop lines...
      this.setAndTransferState({
        dropRow: null,
        dropAction: null
      });
    }
  }

  // Are we able to copy from these cells?
  isEmptyRowSelected() {
    return utils.isRowPartOfSelection(this.state.cellSelection, this.props.data.rows.size - 1);
  }

  onCellCut(e) {
    if (e && e.preventDefault && e.stopPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!this.state.cellSelection || this.state.cellSelection.groups.size !== 1) {
      dialogs.alertPrompt("Cut operation not possible with multiple selection ranges");
      return;
    }

    if (this.isEmptyRowSelected()) {
      dialogs.alertPrompt("Cut operation not possible from empty table row");
      return;
    }

    this.onCellCopy(e);
    this.onCellDelete();
  }

  onCellCopy(e) {
    if (e && e.preventDefault && e.stopPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!this.state.cellSelection || this.state.cellSelection.groups.size !== 1) {
      dialogs.alertPrompt("Copy operation not possible with multiple selection ranges");
      return;
    }

    if (this.isEmptyRowSelected()) {
      dialogs.alertPrompt("Copy operation not possible from empty table row");
      return;
    }

    // The selection is a single regular-shaped block
    // We want to get a copy of all of those values
    const columns = this.props.data.columns;
    const group = this.state.cellSelection.groups.first();
    const rows = this.props.data.rows;

    utils.copyTableDataToClipboard(e, columns, group, rows, this.getCellOptions);
  }

  onPaste(e) {
    // Check whether pasting items
    if (isPastingItems(e)) {
      this.props.onItemPaste(e);
    }
    // Pasting to table cells
    else if (this.tableHasFocus() && !isEditable(document.activeElement) && this.state.focusedCell) {
      this.onCellPaste(e);
    }
  }

  onCellPaste(e) {
    if (e && e.preventDefault && e.stopPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }

    const { columns, rows } = this.props.data;
    const cellSelection = this.state.cellSelection;
    const calendar = this.props.calendar;

    const result = utils.pasteDataFromClipboard(e, columns, rows, calendar, cellSelection, this.getCellOptions);
    
    // If the copy failed, show an error message if necessary and exit
    if (!result.success) {
      if (result.errorMessage) {
        dialogs.alertPrompt(result.errorMessage);
      }
      return;
    }

    // Store the paste results so we can update our selection on next receiveProps
    // Note: we now store it every time, and have removed the previous function to
    // check if we have to
    // if (needUpdateSelectionsAfterPaste(result)) {
    //   this._pastedData = result.bulkChanges;
    // }
    this._pastedData = result.bulkChanges;

    // Otherwise, apply the changes
    this.props.onBulkChange(result.newRowIds, result.bulkChanges);
  }

  onCellDelete() {
    const { columns, rows } = this.props.data;
    const cellSelection = this.state.cellSelection;

    const result = utils.bulkChangesForDeletion(columns, rows, cellSelection);

    // If the delete failed, show an error message if necessary and exit
    if (!result.success) {
      if (result.errorMessage) {
        dialogs.alertPrompt(result.errorMessage);
      }
      return;
    }
    // Otherwise, apply the changes
    this.props.onBulkChange(result.newRowIds, result.bulkChanges);
  }

  getCellOptions(columnId, rowId) {
    return this.props.getCellOptions(columnId, rowId);
  }

  onValueChanged(rowIndex, columnId, value) {
    const rowId = this.props.data.rows.get(rowIndex).id;
    // Value changed in empty row means
    // A new row is coming
    // it will impact on the moving focus
    if (rowId === tableRowTypes.EMPTY) {
      this._newRowComing = true;
    }
    return this.props.onValueChanged(rowId, columnId, value);
  }

  onMultiValueChanged(rowIndex, columnId, value, newState) {
    this.props.onMultiValueChanged(this.props.data.rows.get(rowIndex).id, columnId, value, newState);
  }

  getCellId(row, col) {
    return `${this._tableId}-${row}-${col}`;
  }

  requestViewBoundsForZoom(_zoom) {
    return immutableRecords.FlatRectRecord({
      left: 0,
      width: this.props.data.width,
      right: this.props.data.width,
      top: 0,
      height: this.props.data.height,
      bottom: this.props.data.height
    });
  }

  renderCanvas() {
    return (
      <TableCanvasView
        {...this.props}
        key="scrollColumn"
        tableId={this._tableId}
        registerCellFocusCallback={this.registerCellFocusCallback}
        unregisterCellFocusCallback={this.unregisterCellFocusCallback}
        viewId={`${this.props.viewId}-scrollBody`}
        cellSelection={this.state.cellSelection}
        focusedCell={this.state.focusedCell}
        drawDropRow={this.state.dropRow}
        drawDropAction={this.state.dropAction}
        onFocusIn={this.onFocusIn}
        onFocusOut={this.onFocusOut}
        moveFocus={this.moveFocus}
        onLiveRowDrag={this.onLiveRowDrag}
        onDragEnter={this.onDragEnter}
        onDragLeave={this.onDragLeave}
        onRowDrop={this.onRowDrop}
        onCellClick={this.onCellClick}
        onCellDoubleClick={this.onCellClick}
        onCellPopoverToggle={this.onCellPopoverToggle}
        onCellCut={this.onCellCut}
        onCellCopy={this.onCellCopy}
        onCellPaste={this.onPaste}
        onCellDelete={this.onCellDelete}
        onValueChanged={this.onValueChanged}
        onMultiValueChanged={this.onMultiValueChanged}
        getCellId={this.getCellId}
        requestViewBoundsForZoom={this.requestViewBoundsForZoom}
        showRowGutter={true}
        onRowGutterClick={this.onRowGutterClick}
        rowGutterIcon="moveRow"
        rowGutterIconColor="iconFade"
        allowColumnResize={this.props.allowColumnResize}
        onResizeColumn={this.props.onResizeColumn}
        allowColumnReorder={this.props.allowColumnReorder}
        onLiveColumnDrag={this.props.onLiveColumnDrag}
        onColumnDrop={this.props.onColumnDrop}
        allowColumnSelect={this.props.allowColumnSelect}
        onToggleColumnPopover={this.toggleColumnPopover}
        allowSort={this.props.allowSort}
        sortableColumnIds={this.props.sortableColumnIds}
        sortByColumnId={this.props.sortByColumnId}
        sortInDesc={this.props.sortInDesc}
        onSortByColumn={this.props.onSortByColumn}
        getCellOptions={this.getCellOptions}
        requestCanDropItems={this.props.requestCanDropItems}
        adjustItemEndWhenChangingStart={this.props.adjustItemEndWhenChangingStart}
        displayItems={this.props.displayItems}
        requestDragPreviewDataForItem={this.props.requestDragPreviewDataForItem}
        showViolatedConstraints={this.props.showViolatedConstraints}
        pulsateIconId={this.props.pulsateIconId}
        onShowInspector={this.props.onShowInspector}
      />
    );
  }

  render() {
    const cols = this.props.availableColumns
      .map(c => {
        return {
          value: c.id,
          label: c.label,
          selected: Boolean(this.props.data.columns.find(b => b.id === c.id))
        };
      })
      .toList();

    const tableIsActive = this.state.tableHasFocus && this.props.isActiveView;

    return (
      <div className={`tableView ${tableIsActive ? "active" : ""}`} onClick={this.onTableClick}>
        {this.renderCanvas()}
        {this.state.popoverOpen ? (
          <FocusedPopover
            className="tableColumnSelectionPopover"
            placement="bottom-start"
            hideArrow={true}
            boundariesElement="viewport"
            isOpen={this.state.popoverOpen}
            target={`${this._tableId}-toggleColumn`}
            toggle={this.toggleColumnPopover}
            trigger="legacy"
          >
            <PopoverBody onClick={e => e.stopPropagation()}>
              <CheckboxListView items={cols} onChange={this.props.onToggleColumn} />
            </PopoverBody>
          </FocusedPopover>
        ) : null}
      </div>
    );
  }
}

TableView.propTypes = {
  viewId: PropTypes.string.isRequired,
  // The table data.
  data: PropTypeShapes.tableData.isRequired,
  selectedRowIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  availableColumns: PropTypeShapes.availableTableColumnList,
  allowColumnSelect: PropTypes.bool,
  allowColumnResize: PropTypes.bool,
  allowColumnReorder: PropTypes.bool,

  pulsateIconId: PropTypes.string,

  // Can the user sort the table?
  allowSort: PropTypes.bool,
  sortableColumnIds: ImmutablePropTypes.listOf(PropTypes.string),
  sortByColumnId: PropTypes.string,
  sortInDesc: PropTypes.bool,
  onSortByColumn: PropTypes.func,

  // Paste
  onItemPaste: PropTypes.func,

  // Can the user drag rows
  allowRowDrag: PropTypes.bool,
  // Can the user drag rows to create nested groups.
  // If Yes, indicators will be used to show where an item should drop.
  // If No, rows will move out of the way and leave a gap where they belong.
  allowRowDragNesting: PropTypes.bool,
  // What rows are currently being dragged...?
  draggedRowIds: ImmutablePropTypes.list,

  // How many columns on the left are locked in place?
  lockedColumnCount: PropTypes.number.isRequired,
  // How much space does this view take up?
  overscroll: PropTypes.number,
  calendar: PropTypeShapes.calendar,
  autoAdjustEndDate: PropTypes.bool,

  // Called whenever cell selection is changed.
  // Can be used to change selected item etc.
  onSelectRows: PropTypes.func.isRequired,

  // Called whenever the table receives focus
  // Should be a signal to the owner to become the active view
  onReceivedFocus: PropTypes.func,

  // Called whenever a user clicks to toggle a parent.
  // Expect the view to go and reload the remaining data...?
  onToggleChildren: PropTypes.func.isRequired,
  // Called whenever a value is changed for a cell
  onValueChanged: PropTypes.func.isRequired,
  onMultiValueChanged: PropTypes.func.isRequired,
  onBulkChange: PropTypes.func.isRequired,
  onToggleColumn: PropTypes.func,
  onResizeColumn: PropTypes.func,
  onLiveColumnDrag: PropTypes.func,
  onColumnDrop: PropTypes.func,
  // Dragging rows
  onDragLeave: PropTypes.func,
  onStartRowDrag: PropTypes.func,
  onLiveRowDrag: PropTypes.func,
  onRowDrop: PropTypes.func,
  onCanDrop: PropTypes.func,
  canItemDropOnRow: PropTypes.func.isRequired,
  requestCanDropItems: PropTypes.func.isRequired,
  canTypeHaveSuperType: PropTypes.func.isRequired,
  canRowDrop: PropTypes.bool.isRequired,
  getCellOptions: PropTypes.func,
  adjustItemEndWhenChangingStart: PropTypes.bool,
  displayItems: ImmutablePropTypes.map,
  requestDragPreviewDataForItem: PropTypes.func.isRequired,
  showViolatedConstraints: PropTypes.bool,
  onShowInspector: PropTypes.func,
  disableReordering: PropTypes.bool,
  reorderSortByColumn: PropTypes.string,

  // Should it be responding to keyboard commands?
  isActiveView: PropTypes.bool.isRequired,

  //#!if iOS === true
// isEditMode: PropTypes.bool
  //#!endif
};

export default TableView;
